import React, { useState } from 'react';
import { NotificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationTest = () => {
  const { currentUser, userProfile } = useAuth();
  const { loadNotifications } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const simulateBalanceCredit = async () => {
    if (!currentUser?.uid) {
      setMessage('Vous devez être connecté');
      return;
    }

    setLoading(true);
    try {
      await NotificationService.notifyBalanceCredited(
        currentUser.uid,
        5000,
        'admin123'
      );
      
      // Recharger les notifications
      await loadNotifications();
      
      setMessage('Notification de crédit de solde créée avec succès!');
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('Erreur lors de la création de la notification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const simulateSystemUpdate = async () => {
    if (!currentUser?.uid) {
      setMessage('Vous devez être connecté');
      return;
    }

    setLoading(true);
    try {
      await NotificationService.notifySystemUpdate(
        currentUser.uid,
        'Mise à jour système',
        'Le système a été mis à jour avec de nouvelles fonctionnalités',
        { version: '2.1.0' }
      );
      
      // Recharger les notifications
      await loadNotifications();
      
      setMessage('Notification de mise à jour système créée avec succès!');
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('Erreur lors de la création de la notification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const simulateRechargeRejected = async () => {
    if (!currentUser?.uid) {
      setMessage('Vous devez être connecté');
      return;
    }

    setLoading(true);
    try {
      await NotificationService.notifyRechargeRejected(
        currentUser.uid,
        3000,
        'Documents insuffisants'
      );
      
      // Recharger les notifications
      await loadNotifications();
      
      setMessage('Notification de rejet de recharge créée avec succès!');
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('Erreur lors de la création de la notification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Test des Notifications</h2>
          <p className="text-gray-600">Vous devez être connecté pour tester les notifications.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Test des Notifications</h1>
          
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Utilisateur connecté:</strong> {userProfile?.email || currentUser.email}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <strong>Rôle:</strong> {userProfile?.role || 'Non défini'}
            </p>
          </div>

          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Simuler un crédit de solde</h3>
              <p className="text-sm text-gray-600 mb-3">
                Simule une notification envoyée quand un admin approuve une recharge et crédite le solde.
              </p>
              <button
                onClick={simulateBalanceCredit}
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Création...' : 'Simuler crédit de 5,000 DZD'}
              </button>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Simuler une mise à jour système</h3>
              <p className="text-sm text-gray-600 mb-3">
                Simule une notification de mise à jour du système.
              </p>
              <button
                onClick={simulateSystemUpdate}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Création...' : 'Simuler mise à jour système'}
              </button>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Simuler un rejet de recharge</h3>
              <p className="text-sm text-gray-600 mb-3">
                Simule une notification envoyée quand un admin rejette une demande de recharge.
              </p>
              <button
                onClick={simulateRechargeRejected}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Création...' : 'Simuler rejet de recharge'}
              </button>
            </div>
          </div>

          {message && (
            <div className={`mt-6 p-4 rounded-md ${
              message.includes('succès') 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <h4 className="text-sm font-semibold text-yellow-800 mb-2">Instructions:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Cliquez sur un bouton pour créer une notification</li>
              <li>• Regardez la cloche de notification en haut à droite</li>
              <li>• Un point rouge devrait apparaître avec le nombre de notifications non lues</li>
              <li>• Cliquez sur la cloche pour voir les notifications</li>
              <li>• Marquez les notifications comme lues pour faire disparaître le point rouge</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationTest;